import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import campaignParticipantService from './campaignParticipantService';
import notificationService from './notificationService';

/**
 * Payment Service - Handles all operations related to payments between brands and influencers
 */
const paymentService = {
  /**
   * Create a payment record
   * @param {string} brandId - Brand user ID
   * @param {string} influencerId - Influencer user ID
   * @param {string} campaignId - Campaign ID
   * @param {string} participantId - Campaign participant ID
   * @param {number} amount - Payment amount
   * @param {string} paymentMethod - Payment method
   * @param {object} paymentDetails - Additional payment details
   * @returns {Promise<object>} - Created payment object with ID
   */
  createPayment: async (
    brandId, 
    influencerId, 
    campaignId, 
    participantId, 
    amount, 
    paymentMethod, 
    paymentDetails = {}
  ) => {
    try {
      const paymentsRef = collection(db, 'payments');
      
      const paymentData = {
        brandId,
        influencerId,
        campaignId,
        participantId,
        amount,
        paymentMethod,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...paymentDetails
      };
      
      const docRef = await addDoc(paymentsRef, paymentData);
      
      return { id: docRef.id, ...paymentData };
    } catch (error) {
      console.error('Error creating payment:', error);
      throw error;
    }
  },

  /**
   * Get payment by ID
   * @param {string} paymentId - Payment ID
   * @returns {Promise<object|null>} - Payment data or null if not found
   */
  getPaymentById: async (paymentId) => {
    try {
      const paymentRef = doc(db, 'payments', paymentId);
      const paymentDoc = await getDoc(paymentRef);
      
      if (paymentDoc.exists()) {
        return { id: paymentDoc.id, ...paymentDoc.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting payment:', error);
      throw error;
    }
  },

  /**
   * Update payment status
   * @param {string} paymentId - Payment ID
   * @param {string} status - New status ('completed', 'failed', etc.)
   * @param {object} additionalData - Additional data to update
   * @returns {Promise<object>} - Updated payment object
   */
  updatePaymentStatus: async (paymentId, status, additionalData = {}) => {
    try {
      const paymentRef = doc(db, 'payments', paymentId);
      const paymentDoc = await getDoc(paymentRef);
      
      if (!paymentDoc.exists()) {
        throw new Error(`Payment with ID ${paymentId} not found`);
      }
      
      const payment = paymentDoc.data();
      
      const updateData = {
        status,
        updatedAt: serverTimestamp(),
        ...additionalData
      };
      
      await updateDoc(paymentRef, updateData);
      
      // If payment is completed, update participant payment status
      if (status === 'completed') {
        await campaignParticipantService.updatePaymentStatus(
          payment.participantId,
          'paid',
          paymentId
        );
        
        // Create notification for influencer
        await notificationService.createPaymentNotification(
          payment.influencerId,
          payment.campaignId,
          payment.campaignTitle || 'Campaign',
          paymentId,
          payment.amount
        );
      }
      
      return { 
        id: paymentId, 
        ...payment, 
        ...updateData 
      };
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  },

  /**
   * Process payment (simulate payment processing)
   * @param {string} paymentId - Payment ID
   * @param {object} paymentDetails - Payment processing details
   * @returns {Promise<object>} - Updated payment object
   */
  processPayment: async (paymentId, paymentDetails = {}) => {
    try {
      const payment = await paymentService.getPaymentById(paymentId);
      
      if (!payment) {
        throw new Error(`Payment with ID ${paymentId} not found`);
      }
      
      if (payment.status === 'completed') {
        throw new Error('Payment has already been completed');
      }
      
      // In a real app, this would integrate with a payment gateway
      // For now, we'll simulate a successful payment
      const transactionId = 'txn_' + Math.random().toString(36).substring(2, 15);
      
      const updateData = {
        status: 'completed',
        transactionId,
        processedAt: serverTimestamp(),
        ...paymentDetails
      };
      
      return await paymentService.updatePaymentStatus(paymentId, 'completed', updateData);
    } catch (error) {
      console.error('Error processing payment:', error);
      throw error;
    }
  },

  /**
   * Get payments by brand ID
   * @param {string} brandId - Brand user ID
   * @param {object} options - Query options (status, campaignId, etc.)
   * @returns {Promise<Array>} - Array of payment objects
   */
  getPaymentsByBrandId: async (brandId, options = {}) => {
    try {
      const paymentsRef = collection(db, 'payments');
      
      // Build query
      let q = query(paymentsRef, where('brandId', '==', brandId));
      
      // Add status filter if provided
      if (options.status) {
        q = query(q, where('status', '==', options.status));
      }
      
      // Add campaign filter if provided
      if (options.campaignId) {
        q = query(q, where('campaignId', '==', options.campaignId));
      }
      
      // Add ordering
      const orderByField = options.orderBy || 'createdAt';
      const orderDirection = options.orderDirection || 'desc';
      q = query(q, orderBy(orderByField, orderDirection));
      
      const querySnapshot = await getDocs(q);
      
      const payments = [];
      querySnapshot.forEach((doc) => {
        payments.push({ id: doc.id, ...doc.data() });
      });
      
      return payments;
    } catch (error) {
      console.error('Error getting payments by brand ID:', error);
      throw error;
    }
  },

  /**
   * Get payments by influencer ID
   * @param {string} influencerId - Influencer user ID
   * @param {object} options - Query options (status, campaignId, etc.)
   * @returns {Promise<Array>} - Array of payment objects
   */
  getPaymentsByInfluencerId: async (influencerId, options = {}) => {
    try {
      const paymentsRef = collection(db, 'payments');
      
      // Build query
      let q = query(paymentsRef, where('influencerId', '==', influencerId));
      
      // Add status filter if provided
      if (options.status) {
        q = query(q, where('status', '==', options.status));
      }
      
      // Add campaign filter if provided
      if (options.campaignId) {
        q = query(q, where('campaignId', '==', options.campaignId));
      }
      
      // Add ordering
      const orderByField = options.orderBy || 'createdAt';
      const orderDirection = options.orderDirection || 'desc';
      q = query(q, orderBy(orderByField, orderDirection));
      
      const querySnapshot = await getDocs(q);
      
      const payments = [];
      querySnapshot.forEach((doc) => {
        payments.push({ id: doc.id, ...doc.data() });
      });
      
      return payments;
    } catch (error) {
      console.error('Error getting payments by influencer ID:', error);
      throw error;
    }
  },

  /**
   * Get payments by campaign ID
   * @param {string} campaignId - Campaign ID
   * @param {object} options - Query options (status, etc.)
   * @returns {Promise<Array>} - Array of payment objects
   */
  getPaymentsByCampaignId: async (campaignId, options = {}) => {
    try {
      const paymentsRef = collection(db, 'payments');
      
      // Build query
      let q = query(paymentsRef, where('campaignId', '==', campaignId));
      
      // Add status filter if provided
      if (options.status) {
        q = query(q, where('status', '==', options.status));
      }
      
      // Add ordering
      const orderByField = options.orderBy || 'createdAt';
      const orderDirection = options.orderDirection || 'desc';
      q = query(q, orderBy(orderByField, orderDirection));
      
      const querySnapshot = await getDocs(q);
      
      const payments = [];
      querySnapshot.forEach((doc) => {
        payments.push({ id: doc.id, ...doc.data() });
      });
      
      return payments;
    } catch (error) {
      console.error('Error getting payments by campaign ID:', error);
      throw error;
    }
  },

  /**
   * Calculate total payments for a brand
   * @param {string} brandId - Brand user ID
   * @param {object} options - Query options (status, timeframe, etc.)
   * @returns {Promise<number>} - Total payment amount
   */
  calculateTotalPaymentsByBrand: async (brandId, options = {}) => {
    try {
      const payments = await paymentService.getPaymentsByBrandId(brandId, options);
      
      // Calculate total amount
      const totalAmount = payments.reduce((sum, payment) => {
        // Only include completed payments in total
        if (payment.status === 'completed' || !options.completedOnly) {
          return sum + payment.amount;
        }
        return sum;
      }, 0);
      
      return totalAmount;
    } catch (error) {
      console.error('Error calculating total payments by brand:', error);
      throw error;
    }
  },

  /**
   * Calculate total earnings for an influencer
   * @param {string} influencerId - Influencer user ID
   * @param {object} options - Query options (status, timeframe, etc.)
   * @returns {Promise<number>} - Total earnings amount
   */
  calculateTotalEarningsByInfluencer: async (influencerId, options = {}) => {
    try {
      const payments = await paymentService.getPaymentsByInfluencerId(influencerId, options);
      
      // Calculate total amount
      const totalAmount = payments.reduce((sum, payment) => {
        // Only include completed payments in total
        if (payment.status === 'completed' || !options.completedOnly) {
          return sum + payment.amount;
        }
        return sum;
      }, 0);
      
      return totalAmount;
    } catch (error) {
      console.error('Error calculating total earnings by influencer:', error);
      throw error;
    }
  }
};

export default paymentService;
